import React, { useState, useRef, useEffect } from 'react';
import './AccountButton.css';
import AccountDropdown, { type AccountDropdownItem } from './AccountDropdown';
import { useUnreadMessages } from '../contexts/UnreadMessagesContext';
import { navigateToAccountTab, requestAccountTabReveal } from '../utils/accountTabReveal';
import { getAppRoute, type AppPage } from '../utils/appRoutes';
import { ChevronDown } from 'lucide-react';

const accountDropdownItems: Partial<Record<AppPage, AccountDropdownItem>> = {
  profile: 'profile',
  myAnnouncements: 'announcements',
  favorites: 'favorites',
  messages: 'messages',
  orders: 'orders',
  orderStatistics: 'orderStatistics',
  orderReport: 'orderReport',
  locations: 'locations',
  administration: 'administration',
  settings: 'settings',
};

interface AccountButtonProps {
  text?: string;
  isAdmin?: boolean;
  onProfileClick?: () => void;
  onFavoritesClick?: () => void;
  onLocationsClick?: () => void;
  onMessagesClick?: () => void;
  onAnnouncementsClick?: () => void;
  onAdministrationClick?: () => void;
  onSettingsClick?: () => void;
  onLogoutClick?: () => void;
}

const AccountButton: React.FC<AccountButtonProps> = ({
  text = 'Contul meu',
  isAdmin = false,
  onProfileClick,
  onFavoritesClick,
  onLocationsClick,
  onMessagesClick,
  onAnnouncementsClick,
  onAdministrationClick,
  onSettingsClick,
  onLogoutClick
}) => {
  const [expanded, setExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { hasUnreadMessages } = useUnreadMessages();
  const activeItem = accountDropdownItems[getAppRoute().page];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setExpanded(false);
      }
    };

    const handleScroll = () => {
      setExpanded(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleToggle = () => {
    setExpanded(!expanded);
  };

  return (
    <div className="account-button-container" ref={containerRef}>
      <button
        type="button"
        className={`account-button ${expanded ? 'expanded' : ''}`}
        onClick={handleToggle}
        aria-expanded={expanded}
        aria-haspopup={true}
      >
        <div className="account-button-label">
          <span>{text}</span>
        </div>
        <ChevronDown className="account-button-chevron" aria-hidden="true" />
      </button>
      {hasUnreadMessages && <span className="account-button-notification-dot" aria-hidden="true" />}
      {expanded && (
        <AccountDropdown
          activeItem={activeItem}
          hasUnreadMessages={hasUnreadMessages}
          isAdmin={isAdmin}
          onProfileClick={() => {
            requestAccountTabReveal('profile');
            onProfileClick?.();
            setExpanded(false);
          }}
          onFavoritesClick={() => {
            if (onFavoritesClick) {
              requestAccountTabReveal('favorites');
              onFavoritesClick();
            } else {
              navigateToAccountTab('favorites');
            }
            setExpanded(false);
          }}
          onLocationsClick={() => {
            requestAccountTabReveal('locations');
            onLocationsClick?.();
            setExpanded(false);
          }}
          onOrdersClick={() => {
            navigateToAccountTab('orders');
            setExpanded(false);
          }}
          onOrderStatisticsClick={() => {
            navigateToAccountTab('orderStatistics');
            setExpanded(false);
          }}
          onOrderReportClick={() => {
            navigateToAccountTab('orderReport');
            setExpanded(false);
          }}
          onMessagesClick={() => {
            requestAccountTabReveal('messages');
            onMessagesClick?.();
            setExpanded(false);
          }}
          onAnnouncementsClick={() => {
            requestAccountTabReveal('announcements');
            onAnnouncementsClick?.();
            setExpanded(false);
          }}
          onAdministrationClick={() => {
            onAdministrationClick?.();
            setExpanded(false);
          }}
          onSettingsClick={() => {
            if (onSettingsClick) {
              requestAccountTabReveal('settings');
              onSettingsClick();
            } else {
              navigateToAccountTab('settings');
            }
            setExpanded(false);
          }}
          onLogoutClick={() => {
            onLogoutClick?.();
            setExpanded(false);
          }}
        />
      )}
    </div>
  );
};

export default AccountButton;
